import { Tx, STATUS, fmtUSD, fmtHTG } from "../data"
import { avatarUri, inferPresentation } from "../avatar"
import { C, FONT, F, S } from "../tokens"

// ═══════════════════════════════════════════════════════════════════════════
// Real receipt export — canvas-rendered PNG/PDF built from the actual Tx
// object TxSheet already holds (real history row or this-session
// submission alike), never a template.
//
// SINGLE SOURCE OF TRUTH, twice over:
//   - The avatar is generated by the exact same avatarUri() the in-app
//     Avatar component calls — not a second DiceBear integration that
//     could drift from it.
//   - Every icon is drawn from the SAME <path d="..."> strings Icon() in
//     prims.tsx returns for "hourglass"/"send"/"user"/"card"/"clock"/
//     "circleCheck"/"circleX" — copied here as literal path data (canvas
//     has no way to render a React component), not redrawn by eye. If
//     prims.tsx's paths ever change, these go stale in a way nothing
//     will flag — that's the one seam left where "single source of
//     truth" is aspirational rather than structural, given a <canvas>
//     can't import a React icon component.
// ═══════════════════════════════════════════════════════════════════════════

const KIND_LABEL: Record<Tx["kind"], string> = { depo: "Depo", retre: "Retrè", transfe: "Transfè" }
// Same literal values as C.ok/warn/danger/okSoft/warnSoft/dangerSoft in
// tokens.tsx — imported directly instead of duplicated, so a palette
// change can't silently drift the receipt out of sync with the app.
const TONE_COLOR: Record<string, string> = { ok: C.ok, warn: C.warn, danger: C.danger, info: C.tealDeep }
const TONE_BG: Record<string, string> = { ok: C.okSoft, warn: C.warnSoft, danger: C.dangerSoft, info: "rgba(26,157,139,0.12)" }

/** The app's two real brand typefaces (tokens.tsx). General Sans is the
 *  base for headings/labels/names — FONT.display is what `.app` itself
 *  sets as its font-family. Atkinson Hyperlegible (FONT.numeric, despite
 *  the name) is what styles.tsx opts specific values into wherever a
 *  reader needs to scan exact characters — amounts, dates, references,
 *  emails/handles (.hero-num, .rev-v, .pf-e, .row-sub, …) — never a
 *  system-font fallback chain of its own. */
const DISPLAY = FONT.display
const NUMERIC = FONT.numeric

/** Canvas text uses whatever font faces are already in document.fonts at
 *  the moment fillText runs — unlike DOM text, it does NOT wait on
 *  @font-face's own font-display timeline. Without this, the first
 *  receipt export after a cold page load can silently draw in a fallback
 *  system font for the one canvas that's supposed to look most like a
 *  real Payvit document. Loads the exact three faces index.html declares
 *  (General Sans 200-700 variable, Atkinson 400 and 700 as two discrete
 *  static faces) and waits for the browser's font-loading queue to
 *  settle before any drawing starts. */
let fontsReady: Promise<unknown> | null = null
function ensureFontsLoaded(): Promise<unknown> {
    if (!fontsReady) {
        fontsReady = (async () => {
            if (!("fonts" in document)) return
            try {
                await Promise.all([
                    document.fonts.load(`700 16px ${DISPLAY}`),
                    document.fonts.load(`400 16px ${NUMERIC}`),
                    document.fonts.load(`700 16px ${NUMERIC}`),
                ])
                await document.fonts.ready
            } catch (_e) {
                // best-effort — worst case this one export falls back
                // to the stack's next font for a frame
            }
        })()
    }
    return fontsReady
}

/** Literal copies of prims.tsx's Icon() path data, 24x24 viewBox — see the
 *  file header for why these can't just import the component. */
const ICON_PATHS: Record<string, string[]> = {
    hourglass: ["M7 3h10", "M7 21h10", "M8 3v3.5L12 11l4-4.5V3", "M8 21v-3.5L12 13l4 4.5V21"],
    circleCheck: ["M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0", "M8.4 12.2l2.5 2.5 4.7-4.9"],
    circleX: ["M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0", "M14.9 9.1l-5.8 5.8", "M9.1 9.1l5.8 5.8"],
    send: ["M14.5 21.7a.5.5 0 0 0 .9 0l6.5-19a.5.5 0 0 0-.6-.6l-19 6.5a.5.5 0 0 0 0 .9l7.9 3.2a2 2 0 0 1 1.1 1.1z", "M21.9 2.2 11 13.1"],
    user: ["M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2", "M12 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"],
    card: ["M2 5h20v14h-20z", "M2 10h20", "M6 15h4"],
    clock: ["M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0", "M12 7v5l3.2 1.8"],
    wallet: ["M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1", "M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"],
}
/** hourglass/circleCheck/circleX's second path ("...a9 9 0 1 0...") is the
 *  circle rewritten as an arc pair so it composites into the same stroked
 *  Path2D as everything else — SVG's <circle> has no path-data equivalent,
 *  and mixing a canvas arc() call into an otherwise-uniform path-stroke
 *  loop would need its own special case for exactly three icons. */
const STATUS_ICON: Record<string, string> = { ok: "circleCheck", warn: "hourglass", danger: "circleX", info: "hourglass" }

function drawIcon(ctx: CanvasRenderingContext2D, name: string, cx: number, cy: number, size: number, color: string, strokeWidth = 1.8) {
    const paths = ICON_PATHS[name]
    if (!paths) return
    ctx.save()
    ctx.translate(cx - size / 2, cy - size / 2)
    ctx.scale(size / 24, size / 24)
    ctx.strokeStyle = color
    ctx.lineWidth = strokeWidth
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    for (const d of paths) ctx.stroke(new Path2D(d))
    ctx.restore()
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
}

/** The app's real card surface, translated to canvas — tokens.tsx's
 *  GLASS_CSS .g-card is a diagonal (158deg) tri-stop white→mint gradient
 *  fill with a border that's brighter on its top/left edge than its
 *  bottom/right (light catching the glass), plus a soft drop shadow.
 *  Canvas can't backdrop-blur what's behind it, but the gradient +
 *  differentiated border + shadow are exactly what reads as "glass" even
 *  in a flattened PNG/PDF — a flat white fill with a uniform border,
 *  which is what this file used before, is what read as a generic
 *  template card instead of this app's own card. */
function drawGlassCard(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.save()
    ctx.shadowColor = "rgba(7,40,38,.30)"
    ctx.shadowBlur = 26
    ctx.shadowOffsetY = 12
    roundRect(ctx, x, y, w, h, r)
    const grad = ctx.createLinearGradient(x, y, x + w * 0.55, y + h)
    grad.addColorStop(0, "rgba(255,255,255,.80)")
    grad.addColorStop(0.52, "rgba(240,255,252,.56)")
    grad.addColorStop(1, "rgba(228,250,245,.48)")
    ctx.fillStyle = grad
    ctx.fill()
    ctx.restore()

    roundRect(ctx, x, y, w, h, r)
    ctx.strokeStyle = "rgba(255,255,255,.60)"
    ctx.lineWidth = 1.5
    ctx.stroke()

    // Brighter top+left highlight overdraw — the .g-card detail
    // (border-top-color/border-left-color) that a uniform stroke misses.
    ctx.save()
    roundRect(ctx, x, y, w, h, r)
    ctx.clip()
    ctx.strokeStyle = "rgba(255,255,255,.98)"
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(x, y + h * 0.4)
    ctx.lineTo(x, y + r)
    ctx.arcTo(x, y, x + r, y, r)
    ctx.lineTo(x + w * 0.7, y)
    ctx.stroke()
    ctx.restore()
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => resolve(null)
        img.src = src
    })
}

/** Encodes the reference as plain text, not a URL — this app has no
 *  per-transaction page to deep-link to (no routing exists for `/tx/:nit`),
 *  and printing a URL that resolves to nothing but the app's front door
 *  would be a QR code that looks functional but isn't. A scanned reference
 *  someone can read or paste into a support chat is real; a fake deep link
 *  is not. */
function drawQr(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, dark: string) {
    let QRCode: any
    try {
        QRCode = require("qrcode")
    } catch (_e) {
        return
    }
    const qr = QRCode.create(text, { errorCorrectionLevel: "M" })
    const n = qr.modules.size
    const cell = size / n
    ctx.fillStyle = dark
    for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
            if (qr.modules.data[row * n + col]) {
                ctx.fillRect(x + col * cell, y + row * cell, cell + 0.5, cell + 0.5)
            }
        }
    }
}

const cardW = 640

/** Who the two identity rows name depends on which way the money moved —
 *  a deposit's "source" is the external rail, an outbound transfer's
 *  "source" is the account itself. Both sides are real fields (the
 *  counterparty already on `tx`, or the real signed-in user passed in),
 *  never invented. */
function directionRows(tx: Tx, currentUser: { name: string; email: string }): { to: string; from: string } {
    const you = currentUser.name || currentUser.email.split("@")[0]
    const isOutbound = tx.kind !== "depo"
    return isOutbound
        ? { to: tx.name || tx.handle || "—", from: you }
        : { to: you, from: tx.name || tx.method || "—" }
}

async function buildReceiptCanvas(tx: Tx, currentUser: { name: string; email: string }): Promise<HTMLCanvasElement> {
    const tone = STATUS[tx.statut].tone
    const toneColor = TONE_COLOR[tone]
    const toneBg = TONE_BG[tone]
    const statusIcon = STATUS_ICON[tone]
    const { to, from } = directionRows(tx, currentUser)

    const rows: [string, string, string][] = [
        ["hourglass", "Estati", STATUS[tx.statut].label],
        ["send", "Voye bay", to],
        ["user", "Sòti nan", from],
        ["card", "Metòd", tx.method],
        ["clock", "Dat", tx.at],
    ]
    if (tx.note) rows.push(["card", "Nòt", tx.note])
    if (tx.fee != null) rows.push(["wallet", "Frè", fmtUSD(tx.fee)])

    // 40 → 52: at 40, an icon centered 8px above the row's midpoint (the
    // row loop's old offset) sat only ~3px below the divider from the
    // row above it. Centering the icon on the row's true midpoint (see
    // below) and growing the row to 52 gives every icon/text ~17px of
    // clear space above and below before the next hairline.
    const rowH = 52
    const summaryH = rows.length * rowH + 32
    const qrBlockH = 168

    /* The exact final height depends on conditional content (name/handle
       lines, the rate line, note/fee rows) that only the draw pass below
       actually knows about — hand-maintaining a second formula that has
       to match every one of those conditionals is exactly the kind of
       two-sources-of-truth bug this app has been burned by before (see
       the fetchBalanceUsd field-name mismatch). Draws onto a generously
       oversized scratch canvas instead, tracks the real y the drawing
       loop reaches, then crops a correctly-sized final canvas to it —
       one source of truth (the draw calls themselves) for both the
       layout AND the canvas size. */
    const SCRATCH_H = 1400
    const scale = 2
    const scratch = document.createElement("canvas")
    scratch.width = cardW * scale
    scratch.height = SCRATCH_H * scale
    const ctx = scratch.getContext("2d")!
    ctx.scale(scale, scale)
    await ensureFontsLoaded()

    // Background — the app's own mint, with a soft vertical gradient
    // rather than a flat fill, so the card in front of it reads as
    // sitting on something instead of pasted onto a solid border. Fixed
    // span (not tied to the eventual crop height) so it looks the same
    // regardless of how tall a given receipt turns out to be.
    const bg = ctx.createLinearGradient(0, 0, 0, 900)
    bg.addColorStop(0, "#EAFFFC")
    bg.addColorStop(1, "#DCF7F1")
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, cardW, SCRATCH_H)

    const cx = cardW / 2
    let y = 44

    // ── Avatar + status badge ────────────────────────────────────────
    const avR = 46
    const avUri = avatarUri(tx.seed, inferPresentation(tx.name))
    const avImg = avUri ? await loadImage(avUri) : null
    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, y + avR, avR, 0, Math.PI * 2)
    ctx.closePath()
    ctx.fillStyle = C.teal
    ctx.fill()
    if (avImg) {
        ctx.clip()
        ctx.drawImage(avImg, cx - avR, y, avR * 2, avR * 2)
    }
    ctx.restore()
    // Ring, matching the in-app avatar's white halo
    ctx.beginPath()
    ctx.arc(cx, y + avR, avR, 0, Math.PI * 2)
    ctx.strokeStyle = "rgba(255,255,255,.9)"
    ctx.lineWidth = 3
    ctx.stroke()

    const badgeR = 15
    const badgeCx = cx + avR * Math.SQRT1_2
    const badgeCy = y + avR + avR * Math.SQRT1_2
    ctx.beginPath()
    ctx.arc(badgeCx, badgeCy, badgeR, 0, Math.PI * 2)
    ctx.fillStyle = toneColor
    ctx.fill()
    ctx.strokeStyle = "#fff"
    ctx.lineWidth = 3
    ctx.stroke()
    drawIcon(ctx, statusIcon, badgeCx, badgeCy, 16, "#fff", 2.2)

    y += avR * 2 + 18 + badgeR * 0.4

    // Counterparty name — small, muted, real (tx.name), never invented.
    // Identity/heading text: General Sans, matching .row-nm/.sheet-nm.
    if (tx.name) {
        ctx.fillStyle = C.text
        ctx.font = `700 15px ${DISPLAY}`
        ctx.textAlign = "center"
        ctx.fillText(tx.name, cx, y)
        y += 22
    }
    // A handle is an identifier (email/@handle), same role as .pf-e/.rail-em
    // — Atkinson Hyperlegible, like every other identifier on this receipt.
    if (tx.handle && tx.handle !== tx.name) {
        ctx.fillStyle = C.textMute
        ctx.font = `500 12.5px ${NUMERIC}`
        ctx.textAlign = "center"
        ctx.fillText(tx.handle, cx, y)
        y += 20
    }
    y += 8

    // ── Big amount + HTG equivalent ──────────────────────────────────
    // Every big numeric figure elsewhere in the app (.hero-num, .sheet-amt,
    // .pv-tc-amount, .stat-v) is FONT.numeric — Atkinson Hyperlegible.
    const isOut = tx.kind !== "depo"
    ctx.fillStyle = toneColor
    ctx.font = `800 42px ${NUMERIC}`
    ctx.textAlign = "center"
    ctx.fillText(`${isOut ? "−" : "+"}${fmtUSD(tx.amount)}`, cx, y + 36)
    y += 52

    if (tx.rate != null) {
        ctx.fillStyle = C.textMute
        ctx.font = `600 14px ${NUMERIC}`
        ctx.fillText(`≈ ${fmtHTG(tx.amount * tx.rate)}`, cx, y)
        y += 26
    }

    // Status pill — .trk-pill/.vit-chip/.opt-badge all use FONT.numeric
    // for this exact short-badge role.
    const pillLabel = STATUS[tx.statut].label.toUpperCase()
    ctx.font = `700 12px ${NUMERIC}`
    const pillW = ctx.measureText(pillLabel).width + 28
    const pillH = 26
    roundRect(ctx, cx - pillW / 2, y, pillW, pillH, 13)
    ctx.fillStyle = toneBg
    ctx.fill()
    ctx.fillStyle = toneColor
    ctx.textBaseline = "middle"
    ctx.fillText(pillLabel, cx, y + pillH / 2 + 1)
    ctx.textBaseline = "alphabetic"
    ctx.textAlign = "left"
    y += pillH + 34

    // ── "REZIME TRANZAKSYON" + icon-led summary card ─────────────────
    const left = 32
    const right = cardW - 32
    ctx.fillStyle = C.tealDeep
    ctx.font = `800 12px ${DISPLAY}`
    ctx.textAlign = "left"
    const label1 = "REZIME TRANZAKSYON"
    ctx.fillText(label1, left, y)
    const l1w = ctx.measureText(label1).width
    ctx.strokeStyle = C.teal
    ctx.lineWidth = 2
    ctx.beginPath(); ctx.moveTo(left, y + 6); ctx.lineTo(left + l1w, y + 6); ctx.stroke()
    ctx.lineWidth = 1
    y += 20

    drawGlassCard(ctx, left, y, right - left, summaryH, 18)

    let ry = y + 16
    const rowLeft = left + 20
    const rowRight = right - 20
    for (const [icon, k, v] of rows) {
        // The Estati row carries the same tone as the badge/amount above
        // it — status-aware color theming applies to every place status
        // shows up on the receipt, not just the two most visually
        // prominent ones.
        const isStatusRow = k === "Estati"
        const rowCy = ry + rowH / 2
        // Icon centered on the row's true vertical middle — the previous
        // -8px offset (borrowed from a taller row height) is exactly
        // what pushed every icon into the divider above it once rowH
        // shrank; centered + a taller row is what actually fixes it.
        drawIcon(ctx, icon, rowLeft + 9, rowCy, 18, isStatusRow ? toneColor : C.teal, 1.8)
        // Row label/value match .rev-k/.rev-v exactly: label is
        // FONT.display/13px/textSub, value is FONT.numeric/14px/700/text
        // — the same split every other key/value row in the app uses.
        const textY = rowCy + 5
        ctx.fillStyle = C.textSub
        ctx.font = `400 13px ${DISPLAY}`
        ctx.textAlign = "left"
        ctx.fillText(k, rowLeft + 30, textY)
        ctx.fillStyle = isStatusRow ? toneColor : C.text
        ctx.font = `700 14px ${NUMERIC}`
        ctx.textAlign = "right"
        // Long values (a note, a long method chain) wrap to a second
        // line rather than overrunning the card or silently truncating —
        // this is real customer-entered text, not a fixed-width code.
        const maxW = rowRight - rowLeft - 130
        if (ctx.measureText(v).width > maxW) {
            const words = v.split(" ")
            let line = ""
            const lines: string[] = []
            for (const w of words) {
                const test = line ? `${line} ${w}` : w
                if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w }
                else line = test
            }
            if (line) lines.push(line)
            lines.slice(0, 2).forEach((ln, i) => ctx.fillText(ln, rowRight, textY + (i - (lines.length - 1) / 2) * 15))
        } else {
            ctx.fillText(v, rowRight, textY)
        }
        ctx.textAlign = "left"

        const divY = ry + rowH
        if (divY < y + summaryH - 4) {
            ctx.strokeStyle = "rgba(11,46,44,0.06)"
            ctx.beginPath(); ctx.moveTo(rowLeft, divY); ctx.lineTo(rowRight, divY); ctx.stroke()
        }
        ry += rowH
    }
    y += summaryH + 28

    // ── "NIMEWO TRANZAKSYON" block, with QR ──────────────────────────
    const label2 = "NIMEWO TRANZAKSYON"
    ctx.fillStyle = C.tealDeep
    ctx.font = `800 12px ${DISPLAY}`
    ctx.fillText(label2, left, y)
    const l2w = ctx.measureText(label2).width
    ctx.strokeStyle = C.teal
    ctx.lineWidth = 2
    ctx.beginPath(); ctx.moveTo(left, y + 6); ctx.lineTo(left + l2w, y + 6); ctx.stroke()
    ctx.lineWidth = 1
    y += 20

    drawGlassCard(ctx, left, y, right - left, qrBlockH, 18)

    const qrSize = qrBlockH - 40
    drawQr(ctx, `Payvit — Referans: ${tx.nit}`, right - qrSize - 20, y + 20, qrSize, C.text)

    // The reference code is scanned character-by-character just like an
    // amount or a date — Atkinson Hyperlegible, not a generic monospace
    // this brand never actually defines anywhere else.
    ctx.fillStyle = C.text
    ctx.font = `800 17px ${NUMERIC}`
    ctx.fillText(`#${tx.nit}`, rowLeft, y + qrBlockH / 2 + 4)
    ctx.fillStyle = C.textMute
    ctx.font = `500 11.5px ${DISPLAY}`
    ctx.fillText("Montre kòd sa a bay sipò pou yon rechèch rapid", rowLeft, y + qrBlockH / 2 + 26)
    y += qrBlockH + 30

    // ── Footer: real logo mark + domain + disclaimer ─────────────────
    // /logo.png is the same asset PayvitLogo renders everywhere else in
    // the app — but it ships as a 512×512 canvas where the real mark
    // only occupies a ~404×356 region in the middle (measured directly
    // off the file: content bounds x=54-458, y=76-432). Every DOM usage
    // hides that with object-fit:contain; drawImage has no such mode, so
    // drawing the full source into a small box was carrying that baked-in
    // empty margin straight into the receipt — the mark read as smaller
    // and higher than it actually is. This crops to the asset's own real
    // content rect (with a few px of padding so anti-aliased edges
    // aren't clipped) — the exact same mark, never redrawn or recolored,
    // just not wasting the box on transparent pixels.
    const logoImg = await loadImage("/logo.png")
    const LOGO_SRC_X = 50, LOGO_SRC_Y = 72, LOGO_SRC_W = 412, LOGO_SRC_H = 364
    const domainText = "payvitht.com"
    const domainSize = F.small
    ctx.font = `800 ${domainSize}px ${DISPLAY}`
    const textW = ctx.measureText(domainText).width
    // Height-matched to the text at ~1.3× its point size, the same ratio
    // .rail-brand pairs PayvitLogo(26) against its 20px label with — width
    // follows the asset's own cropped aspect ratio so it's never stretched.
    const logoH = domainSize * 1.3
    const logoW = logoImg ? logoH * (LOGO_SRC_W / LOGO_SRC_H) : 0
    const gap = S[2]
    const footerContentW = (logoImg ? logoW + gap : 0) + textW
    let fx = cx - footerContentW / 2
    // Centered on the text's cap-height band, not its baseline — aligning
    // to the baseline (what this used to do) sits an icon visibly too
    // high next to lowercase text; this is what .rail-brand's
    // align-items:center gets for free from the flex box instead.
    const textOpticalCenterY = y - domainSize * 0.36
    if (logoImg) {
        ctx.drawImage(
            logoImg,
            LOGO_SRC_X, LOGO_SRC_Y, LOGO_SRC_W, LOGO_SRC_H,
            fx, textOpticalCenterY - logoH / 2, logoW, logoH
        )
        fx += logoW + gap
    }
    ctx.fillStyle = C.tealDeep
    ctx.textAlign = "left"
    ctx.fillText(domainText, fx, y)
    y += 20

    ctx.fillStyle = C.textMute
    ctx.font = `500 11px ${DISPLAY}`
    ctx.textAlign = "center"
    ctx.fillText("Rezime — se pa yon prèv peman ofisyèl", cx, y)
    ctx.textAlign = "left"

    const finalH = Math.min(y + 24, SCRATCH_H)
    const canvas = document.createElement("canvas")
    canvas.width = cardW * scale
    canvas.height = finalH * scale
    canvas.getContext("2d")!.drawImage(scratch, 0, 0, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height)
    return canvas
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"))
}

export type ReceiptUser = { name: string; email: string }

export async function downloadReceipt(tx: Tx, currentUser: ReceiptUser): Promise<boolean> {
    try {
        const canvas = await buildReceiptCanvas(tx, currentUser)
        const blob = await canvasToBlob(canvas)
        if (!blob) return false
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `payvit-resi-${tx.nit}.png`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 4000)
        return true
    } catch (_e) {
        return false
    }
}

declare global {
    interface Window { __payvitJsPDF?: any }
}

/** jsPDF ships as a physically separate bundle (see build.mjs) — this
 *  loads that script on demand, the first time anyone actually exports a
 *  PDF, rather than every visitor paying for it on every page load. */
function loadJsPdf(): Promise<any> {
    if (window.__payvitJsPDF) return Promise.resolve(window.__payvitJsPDF)
    return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-payvit-pdf]')
        if (existing) {
            existing.addEventListener("load", () => resolve(window.__payvitJsPDF))
            existing.addEventListener("error", () => reject(new Error("pdf bundle failed to load")))
            return
        }
        const script = document.createElement("script")
        script.src = (process.env.PDF_BUNDLE as string) || "/pdf-export.js"
        script.dataset.payvitPdf = "1"
        script.onload = () => resolve(window.__payvitJsPDF)
        script.onerror = () => reject(new Error("pdf bundle failed to load"))
        document.head.appendChild(script)
    })
}

export async function downloadReceiptPdf(tx: Tx, currentUser: ReceiptUser): Promise<boolean> {
    try {
        const canvas = await buildReceiptCanvas(tx, currentUser)
        const jsPDF = await loadJsPdf()
        if (!jsPDF) return false
        const pxToPt = 72 / 96
        const w = (canvas.width / 2) * pxToPt
        const h = (canvas.height / 2) * pxToPt
        const pdf = new jsPDF({ unit: "pt", format: [w, h] })
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, w, h)
        pdf.save(`payvit-resi-${tx.nit}.pdf`)
        return true
    } catch (_e) {
        return false
    }
}

export type ShareOutcome = "shared" | "copied" | "cancelled" | "failed"

export async function shareReceipt(tx: Tx, currentUser: ReceiptUser): Promise<ShareOutcome> {
    const summary = `Resi Payvit\n${tx.name || tx.method} — ${fmtUSD(tx.amount)}\nReferans: ${tx.nit}\nEstati: ${STATUS[tx.statut].label}\nDat: ${tx.at}`
    try {
        const canvas = await buildReceiptCanvas(tx, currentUser)
        const blob = await canvasToBlob(canvas)
        if (blob) {
            const file = new File([blob], `payvit-resi-${tx.nit}.png`, { type: "image/png" })
            const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean }
            if (nav.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: "Resi Payvit", text: summary })
                return "shared"
            }
        }
        if (navigator.share) {
            await navigator.share({ title: "Resi Payvit", text: summary })
            return "shared"
        }
    } catch (e: any) {
        if (e?.name === "AbortError") return "cancelled"
    }
    try {
        await navigator.clipboard.writeText(summary)
        return "copied"
    } catch (_e) {
        return "failed"
    }
}
